import type { RevenueBasis, RevenueGranularity, RevenueSeries, RevenueSummary, TrendBucket, TrendMarker } from '@/api/revenue'
import { Card, EmptyState, formatValue, NARROW_QUERY, SegmentedControl, useMediaQuery, type FormatOptions } from '@/design'
import { ChangeText } from '@/features/overview/ChangeText'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { formatDateKey, type GlobalFilters } from '@/lib/filters'

import { comparisonLabel, PREVIOUS_UNAVAILABLE_TEXT, REVENUE_LABEL } from '../format'
import { GRANULARITY_LABELS, TREND_METRICS, useTrendGranularity, useTrendMetric, type TrendMetric } from '../params'
import { useRevenueSeries } from '../queries'
import { RevenueTrendChart, type TrendChartBucket } from './RevenueTrendChart'

const METRIC_LABELS: Record<TrendMetric, string> = { revenue: 'Revenue', orders: 'Orders', aov: 'AOV' }

const TITLES: Record<RevenueGranularity, string> = {
  hour: 'Hourly revenue',
  day: 'Daily revenue',
  week: 'Weekly revenue',
  month: 'Monthly revenue',
}

/** The bucket's wall-clock time as the API wrote it (`2026-08-01T14:00:00+01:00` → `14:00`). */
function wallTime(instant: string): string {
  return instant.slice(11, 16)
}

/** The instant's UTC offset as the API wrote it: `+01:00` → "UTC+01:00", `Z` → "UTC". */
function offsetLabel(instant: string): string {
  const offset = instant.slice(19)
  return offset === 'Z' || offset === '' ? 'UTC' : `UTC${offset}`
}

function bucketLabels(bucket: TrendBucket, granularity: RevenueGranularity, multiDay: boolean, repeated: boolean): { label: string; title: string } {
  switch (granularity) {
    case 'hour': {
      // The day the clocks go back has two 01:00s: the offset tells them apart.
      const time = repeated ? `${wallTime(bucket.start)} ${offsetLabel(bucket.start)}` : wallTime(bucket.start)
      const day = formatDateKey(bucket.firstDay)
      return { label: multiDay ? `${day} ${time}` : time, title: `${day}, ${time}–${wallTime(bucket.end)}` }
    }
    case 'day':
      return { label: formatDateKey(bucket.firstDay), title: formatDateKey(bucket.firstDay, true) }
    case 'week':
      return {
        label: formatDateKey(bucket.firstDay),
        title: `${formatDateKey(bucket.firstDay)} – ${formatDateKey(bucket.lastDay, true)}`,
      }
    case 'month': {
      const month = new Date(`${bucket.firstDay}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' })
      return { label: month, title: `${formatDateKey(bucket.firstDay)} – ${formatDateKey(bucket.lastDay, true)}` }
    }
  }
}

/** Each marker on the bucket its instant falls in. */
function markersIn(bucket: TrendBucket, markers: readonly TrendMarker[]): TrendMarker[] {
  const start = Date.parse(bucket.start)
  const end = Date.parse(bucket.end)
  return markers.filter((marker) => {
    const at = Date.parse(marker.at)
    return at >= start && at < end
  })
}

export function chartBuckets(series: RevenueSeries, metric: Exclude<TrendMetric, 'aov'>): TrendChartBucket[] {
  const multiDay = series.buckets.some((bucket) => bucket.firstDay !== series.buckets[0]?.firstDay)
  const wallHours = new Map<string, number>()
  for (const bucket of series.buckets) {
    const wall = `${bucket.firstDay} ${wallTime(bucket.start)}`
    wallHours.set(wall, (wallHours.get(wall) ?? 0) + 1)
  }
  return series.buckets.map((bucket) => ({
    key: bucket.start,
    ...bucketLabels(bucket, series.granularity, multiDay, (wallHours.get(`${bucket.firstDay} ${wallTime(bucket.start)}`) ?? 0) > 1),
    value: metric === 'revenue' ? bucket.revenue : bucket.orders,
    // The trend compares revenue only: its buckets carry no previous orders.
    previous: metric === 'revenue' ? bucket.previousRevenue : null,
    revenue: bucket.revenue,
    orders: bucket.orders,
    tickets: bucket.tickets,
    markers: markersIn(bucket, series.markers),
  }))
}

/**
 * The trend card: the period's revenue (or orders) by hour, day, week or month under the
 * basis, against the comparison, with launches and draws marked. The headline figure and
 * its change are the summary's, so the card and the KPI row can never disagree. On a
 * phone the comparison line gives way to the change label.
 */
export function TrendPanel({
  filters,
  basis,
  days,
  summary,
  money,
}: {
  filters: GlobalFilters
  basis: RevenueBasis
  /** The period's length in days, which decides the granularities on offer. */
  days: number
  summary: RevenueSummary | undefined
  money: FormatOptions
}) {
  const [metric, setMetric] = useTrendMetric()
  const [granularity, options, setGranularity] = useTrendGranularity(days)
  const series = useRevenueSeries(filters, basis, granularity)
  const narrow = useMediaQuery(NARROW_QUERY)

  const headlineKey = metric === 'orders' ? 'orders' : metric === 'aov' ? 'aov' : basis === 'gross' ? 'revenue_gross' : 'revenue_net'
  const headline = summary?.metrics[headlineKey]
  const format = metric === 'orders' ? 'integer' : 'currency'

  return (
    <Card
      className="rv-panel rv-panel--wide"
      title={TITLES[granularity]}
      subtitle={`${metric === 'revenue' ? REVENUE_LABEL[basis] : METRIC_LABELS[metric]} · tenant time`}
      busy={series.isFetching}
      actions={
        <div className="rv-actions">
          <SegmentedControl
            label="Trend series"
            options={TREND_METRICS.map((value) => ({ value, label: METRIC_LABELS[value] }))}
            value={metric}
            onChange={setMetric}
          />
          {options.length > 1 ? (
            <SegmentedControl
              label="Trend granularity"
              options={options.map((value) => ({ value, label: GRANULARITY_LABELS[value] }))}
              value={granularity}
              onChange={setGranularity}
            />
          ) : null}
        </div>
      }
    >
      {headline && summary ? (
        <p className="rv-headline">
          <span className="rv-headline__value" data-testid="trend-headline">
            {headline.value === null ? '—' : formatValue(headline.value, format, money)}
          </span>
          <ChangeText change={headline.value === null ? null : (headline.delta?.ratio ?? null)} label={comparisonLabel(summary)} />
          {summary.previousUnavailable ? <span className="rv-muted">{PREVIOUS_UNAVAILABLE_TEXT[summary.previousUnavailable]}</span> : null}
        </p>
      ) : null}
      {metric === 'aov' ? (
        <EmptyState
          variant="no-data"
          compact
          title="Not available by day"
          description="The trend gives revenue and orders for each period, not order value; the period’s AOV is above and in the KPI row."
        />
      ) : (
        <CardQueryBody query={series} loadingRows={6} errorDescription="The revenue trend could not be loaded.">
          {({ data }) =>
            data.buckets.length === 0 ? (
              <EmptyState variant="no-results" compact />
            ) : (
              <RevenueTrendChart
                title={`${TITLES[data.granularity]}: ${METRIC_LABELS[metric].toLowerCase()}`}
                buckets={chartBuckets(data, metric)}
                format={format}
                money={money}
                seriesLabel={metric === 'revenue' ? REVENUE_LABEL[basis] : 'Orders'}
                previousLabel={
                  !narrow && metric === 'revenue' && data.previousRange && !data.previousUnavailable ? comparisonLabel(data, 'Previous:') : null
                }
              />
            )
          }
        </CardQueryBody>
      )}
    </Card>
  )
}
